import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { MessageSquare, PenTool, Calendar, Hammer, SearchCheck, Key } from 'lucide-react';
const steps = [{
  title: 'Initial Consultation',
  description: 'We evaluate your space, listen to your ideas, and establish a realistic preliminary budget.',
  icon: <MessageSquare size={24} data-id="element-218" />
}, {
  title: 'Design & Permits',
  description: 'We create detailed plans and handle all the paperwork to obtain the necessary permits.',
  icon: <PenTool size={24} data-id="element-219" />
}, {
  title: 'Planning',
  description: 'We select materials and establish a strict schedule to optimize the work timeline.',
  icon: <Calendar size={24} data-id="element-220" />
}, {
  title: 'Construction',
  description: 'Our expert team executes the work with daily supervision and constant area cleanup.',
  icon: <Hammer size={24} data-id="element-221" />
}, {
  title: 'Inspection',
  description: 'We perform rigorous quality controls and coordinate the required municipal inspections.',
  icon: <SearchCheck size={24} data-id="element-222" />
}, {
  title: 'Delivery',
  description: 'We present the finished project, ensuring your 100% satisfaction before final handover.',
  icon: <Key size={24} data-id="element-223" />
}];
export function Process() {
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <section className="py-24 bg-primary text-white relative overflow-hidden" data-id="element-224">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" style={{
      backgroundImage: 'radial-gradient(#d4a853 1px, transparent 1px)',
      backgroundSize: '30px 30px'
    }} data-id="element-225"></div>

      <div className="container mx-auto px-6 md:px-12 relative z-10" data-id="element-226">
        <div className="text-center max-w-3xl mx-auto mb-20" data-id="element-227">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-228">
            <h2 className="text-accent font-medium tracking-widest uppercase text-sm mb-3" data-id="element-229">
              Our Method
            </h2>
            <h3 className="text-4xl md:text-5xl font-serif font-bold mb-6" data-id="element-230">
              The Remodeling Process
            </h3>
            <p className="text-gray-300 text-lg" data-id="element-231">
              A structured and transparent approach that minimizes stress and
              guarantees predictable, high-quality results.
            </p>
          </motion.div>
        </div>

        <div ref={ref} className="relative max-w-4xl mx-auto" data-id="element-232">
          {/* Central Line for Desktop */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-px bg-primary-light transform -translate-x-1/2" data-id="element-233"></div>

          <div className="space-y-12 md:space-y-0" data-id="element-234">
            {steps.map((step, index) => {
            const isEven = index % 2 === 0;
            return <motion.div key={index} initial={{
              opacity: 0,
              y: 50
            }} animate={inView ? {
              opacity: 1,
              y: 0
            } : {
              opacity: 0,
              y: 50
            }} transition={{
              duration: 0.6,
              delay: index * 0.15
            }} className={`relative flex flex-col md:flex-row items-center ${isEven ? 'md:flex-row-reverse' : ''}`} data-id="element-235">
                  {/* Timeline Dot */}
                  <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-12 h-12 rounded-full bg-primary border-4 border-accent items-center justify-center text-accent z-10" data-id="element-236">
                    <span className="font-bold text-sm" data-id="element-237">{index + 1}</span>
                  </div>

                  {/* Content */}
                  <div className={`w-full md:w-1/2 ${isEven ? 'md:pl-16' : 'md:pr-16'} flex flex-col ${isEven ? 'md:items-start text-left' : 'md:items-end md:text-right text-left'}`} data-id="element-238">
                    <div className="bg-primary-light/50 p-8 rounded-sm border border-primary-light w-full hover:border-accent/50 transition-colors" data-id="element-239">
                      <div className={`flex items-center gap-4 mb-4 ${isEven ? 'flex-row' : 'flex-row md:flex-row-reverse'}`} data-id="element-240">
                        <div className="w-12 h-12 rounded-sm bg-accent/20 text-accent flex items-center justify-center shrink-0" data-id="element-241">
                          {step.icon}
                        </div>
                        <h4 className="text-2xl font-serif font-bold" data-id="element-242">
                          {step.title}
                        </h4>
                      </div>
                      <p className="text-gray-400 leading-relaxed" data-id="element-243">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}